import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "@/data/site";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Our Work", href: "#gallery" },
  { label: "About", href: "#about" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#quote" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3"
        >
          <img
            src={BUSINESS.logo}
            alt="Iqaluit Cleaning & Maintenance logo"
            className="h-12 w-12 object-contain rounded-md bg-white"
          />
          <span className="hidden sm:flex flex-col leading-tight">
            <span
              className={`font-display font-800 text-[15px] tracking-tight ${
                scrolled ? "text-[#0f2452]" : "text-white"
              }`}
              style={{ fontWeight: 800 }}
            >
              Iqaluit Cleaning
            </span>
            <span
              className={`text-[11px] tracking-[0.22em] uppercase font-semibold ${
                scrolled ? "text-[#2563eb]" : "text-sky-300"
              }`}
            >
              &amp; Maintenance
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-link-${n.label.toLowerCase().replace(" ", "-")}`}
              className={`text-[15px] font-medium transition-colors hover:text-[#60a5fa] ${
                scrolled ? "text-slate-700" : "text-white/90"
              }`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BUSINESS.phoneHref}
            data-testid="nav-call-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#2563eb] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1d4ed8] transition-colors shadow-sm"
          >
            <Phone className="h-4 w-4" />
            {BUSINESS.phone}
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-lg border ${
              scrolled
                ? "border-slate-200 bg-white text-[#0f2452]"
                : "border-white/30 bg-white/10 text-white"
            }`}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200 px-5 py-4 space-y-1"
        >
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={() => setOpen(false)}
              className="block py-3 text-base font-medium text-slate-700 border-b border-slate-100 last:border-0"
            >
              {n.label}
            </a>
          ))}
          <a
            href={BUSINESS.phoneHref}
            className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#2563eb] px-5 py-3 text-sm font-semibold text-white"
          >
            <Phone className="h-4 w-4" /> Call {BUSINESS.phone}
          </a>
        </div>
      )}
    </header>
  );
};
